/**
 * Date formatting utilities for German locale
 * Handles multiple input formats and avoids timezone issues
 */

/**
 * Safely parses a date string without timezone interpretation issues.
 * Handles: YYYY-MM-DD, DD.MM.YYYY, ISO strings, and Date objects.
 *
 * When parsing date-only strings like "2024-02-23", JavaScript's Date constructor
 * interprets them as UTC midnight, which can shift the date by one day depending
 * on the local timezone. This function avoids that by manually parsing such strings.
 */
export function parseLocalDate(dateInput: string | Date): Date | null {
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }

  const dateStr = dateInput.trim();

  // Handle ISO date-only format: YYYY-MM-DD or YYYY-M-D (parse manually to avoid UTC)
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      const result = new Date(year, month, day);
      // Validate the date is valid (e.g., Feb 30 would be invalid)
      if (!isNaN(result.getTime()) && result.getDate() === day && result.getMonth() === month) {
        return result;
      }
      return null;
    }
  }

  // Handle German format: DD.MM.YYYY or D.M.YYYY
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateStr)) {
    const parts = dateStr.split('.');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      const result = new Date(year, month, day);
      // Validate the date is valid (e.g., Feb 30 would be invalid)
      if (!isNaN(result.getTime()) && result.getDate() === day && result.getMonth() === month) {
        return result;
      }
      return null;
    }
  }

  // Handle full ISO strings or other formats via Date constructor
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a Date as YYYY-MM-DD using local components (not UTC).
 * Inverse of parseLocalDate for date-only strings — avoids the
 * `toISOString().slice(0, 10)` trap that shifts the date in non-UTC
 * timezones near midnight.
 *
 * @param date - Date object (or null)
 * @returns YYYY-MM-DD string, or null when the input is null/invalid
 */
export function formatLocalISODate(date: Date | null): string | null {
  if (!date || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Monday of the local calendar week containing the given date, at local
 * midnight. Pure local-date arithmetic — no UTC conversion involved.
 *
 * @param date - any Date within the week
 * @returns a new Date at local midnight of that week's Monday
 */
export function mondayOf(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayOfWeek = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dayOfWeek);
  return d;
}

/**
 * Formats a date to German locale with weekday, day, and month.
 * Example: "Montag, 23. Februar"
 *
 * @param dateInput - Date string (YYYY-MM-DD, DD.MM.YYYY, ISO) or Date object
 * @returns Formatted date string or original input if parsing fails
 */
export function formatDateLongGerman(dateInput: string | Date): string {
  const date = parseLocalDate(dateInput);
  if (!date) return String(dateInput);

  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * Formats a date to German locale with day and month only.
 * Example: "23. Februar"
 *
 * @param dateInput - Date string (YYYY-MM-DD, DD.MM.YYYY, ISO) or Date object
 * @returns Formatted date string or original input if parsing fails
 */
export function formatDateShortGerman(dateInput: string | Date): string {
  const date = parseLocalDate(dateInput);
  if (!date) return String(dateInput);

  return date.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
  });
}
